import * as Yup from 'yup';
import { EMisureEControlli, IAttivitaAltoRischio } from '@relab/alstom-ptw-shared';
import i18n from 'i18next';

const AltoRischioMisureControlli: string[] = [
  IAttivitaAltoRischio.spaziConfinati,
  IAttivitaAltoRischio.sicurezzaElettrica,
  IAttivitaAltoRischio.loto,
  IAttivitaAltoRischio.lavoriACaldo,
  IAttivitaAltoRischio.lavoriInQuota,
];

const requiredText = 'Campo richiesto';
const requiredSignText = 'Firma richiesta';

export const headlineValidationSchema = (t: typeof i18n.t) =>
  Yup.object().shape({
    name: Yup.string().required(t(requiredText)),
  });

export const creationValidationSchema = (t: typeof i18n.t) =>
  Yup.object().shape({
    description: Yup.string().required(t(requiredText)),
    attivitaAltoRischio: Yup.array().min(1, ({ min }) => t(`Selezionare almeno`, { min })),
    building: Yup.string().required(t(requiredText)),
    startDate: Yup.date().required(t(requiredText)),
    startHour: Yup.date().required(t(requiredText)),
    endHour: Yup.date().required(t(requiredText)),
    internalExternal: Yup.string().required(t(requiredText)),

    internalEnte: Yup.string().when('internalExternal', {
      is: (values) => values === 'internal',
      then: Yup.string().required(t(requiredText)),
      otherwise: Yup.string(),
    }),
    externalDuvriNum: Yup.string().when('internalExternal', {
      is: (values) => values === 'external',
      then: Yup.string().required(t(requiredText)),
      otherwise: Yup.string(),
    }),
    externalDuvriDate: Yup.date().when('internalExternal', {
      is: (values) => values === 'external',
      then: Yup.date().required(t(requiredText)),
      otherwise: Yup.date(),
    }),
    nominativoResponsabile: Yup.string().required(t(requiredText)),
    nominativoSorveglianteAntincendio: Yup.string().when(
      ['attivitaAltoRischio', 'createdAt'],
      (attivitaAltoRischio: string[] | undefined, createdAt: unknown, schema: Yup.StringSchema) => {
        const isLavoriACaldo =
          Array.isArray(attivitaAltoRischio) && attivitaAltoRischio.includes(IAttivitaAltoRischio.lavoriACaldo);

        const createdDate = createdAt ? new Date(createdAt as any) : undefined;
        // 05/12/2024: Data di aggiunta del campo"
        const threshold = new Date(2024, 11, 5);
        const isAfterThreshold =
          createdDate instanceof Date && !isNaN(createdDate.getTime()) && createdDate >= threshold;

        return isLavoriACaldo && isAfterThreshold ? schema.required(t(requiredText)) : schema;
      }
    ),
    ispezioneArea: Yup.string().required(t(requiredText)),
    dpi: Yup.array(),
    dpiOther: Yup.string().when('dpi', {
      is: (values) => values && values.length > 0,
      then: Yup.string(),
      otherwise: Yup.string().required(t(requiredText)),
    }),
    estinguenti: Yup.array(),
    estinguentiOther: Yup.string(),
    misureEControlli: Yup.array().when('attivitaAltoRischio', {
      is: (values) => values && values.every((value: string) => !AltoRischioMisureControlli.includes(value)),
      then: Yup.array(),
      otherwise: Yup.array().required(t(requiredText)),
    }),
    elettricoTensioneAssente: Yup.boolean(),
    idraulicoTensioneAssente: Yup.boolean(),
    pneumaticoTensioneAssente: Yup.boolean(),
    ossigenoValorePrima: Yup.string().when('misureEControlli', {
      is: (values) => values.includes(EMisureEControlli.ossigeno),
      then: Yup.string().required(t(requiredText)),
      otherwise: Yup.string(),
    }),
    ossigenoValoreDurante: Yup.string().when('misureEControlli', {
      is: (values) => values.includes(EMisureEControlli.ossigeno),
      then: Yup.string().required(t(requiredText)),
      otherwise: Yup.string(),
    }),
    ossigenoValoreTermine: Yup.string().when('misureEControlli', {
      is: (values) => values.includes(EMisureEControlli.ossigeno),
      then: Yup.string().required(t(requiredText)),
      otherwise: Yup.string(),
    }),
    monossidoValorePrima: Yup.string().when('misureEControlli', {
      is: (values) => values.includes(EMisureEControlli.monossido),
      then: Yup.string().required(t(requiredText)),
      otherwise: Yup.string(),
    }),
    monossidoValoreDurante: Yup.string().when('misureEControlli', {
      is: (values) => values.includes(EMisureEControlli.monossido),
      then: Yup.string().required(t(requiredText)),
      otherwise: Yup.string(),
    }),
    monossidoValoreTermine: Yup.string().when('misureEControlli', {
      is: (values) => values.includes(EMisureEControlli.monossido),
      then: Yup.string().required(t(requiredText)),
      otherwise: Yup.string(),
    }),
    metanoValorePrima: Yup.string().when('misureEControlli', {
      is: (values) => values.includes(EMisureEControlli.metano),
      then: Yup.string().required(t(requiredText)),
      otherwise: Yup.string(),
    }),
    metanoValoreDurante: Yup.string().when('misureEControlli', {
      is: (values) => values.includes(EMisureEControlli.metano),
      then: Yup.string().required(t(requiredText)),
      otherwise: Yup.string(),
    }),
    metanoValoreTermine: Yup.string().when('misureEControlli', {
      is: (values) => values.includes(EMisureEControlli.metano),
      then: Yup.string().required(t(requiredText)),
      otherwise: Yup.string(),
    }),
    idrogenoValorePrima: Yup.string().when('misureEControlli', {
      is: (values) => values.includes(EMisureEControlli.idrogeno),
      then: Yup.string().required(t(requiredText)),
      otherwise: Yup.string(),
    }),
    idrogenoValoreDurante: Yup.string().when('misureEControlli', {
      is: (values) => values.includes(EMisureEControlli.idrogeno),
      then: Yup.string().required(t(requiredText)),
      otherwise: Yup.string(),
    }),
    idrogenoValoreTermine: Yup.string().when('misureEControlli', {
      is: (values) => values.includes(EMisureEControlli.idrogeno),
      then: Yup.string().required(t(requiredText)),
      otherwise: Yup.string(),
    }),
    imbracatureSiNo: Yup.boolean().when('misureEControlli', {
      is: (values) => values.includes(EMisureEControlli.imbracature),
      then: Yup.boolean().required(t(requiredText)),
      otherwise: Yup.boolean(),
    }),
    imbracatureDateLast: Yup.date().when('misureEControlli', {
      is: (values) => values.includes(EMisureEControlli.imbracature),
      then: Yup.date().required(t(requiredText)),
      otherwise: Yup.date(),
    }),
    matricolaAnalizzatore: Yup.string().when('attivitaAltoRischio', {
      is: (values) => values.includes(IAttivitaAltoRischio.spaziConfinati),
      then: Yup.string().required(t(requiredText)),
      otherwise: Yup.string(),
    }),
    scadenzaCalibrazione: Yup.string().when('attivitaAltoRischio', {
      is: (values) => values.includes(IAttivitaAltoRischio.spaziConfinati),
      then: Yup.string().required(t(requiredText)),
      otherwise: Yup.string(),
    }),
    evidenzeFormazione: Yup.string().required(t(requiredText)),
    approvazioneDate: Yup.string().required(t(requiredText)),
    approvazioneTime: Yup.string().required(t(requiredText)),
    supervisoreName: Yup.string().required(t(requiredText)),
    supervisoreSign: Yup.string(),
    referenteAreaName: Yup.string().required(t(requiredText)),
    referenteAreaSign: Yup.string(),
    referenteAttivitaName: Yup.string().required(t(requiredText)),
    referenteAttivitaSign: Yup.string(),
    ispezioni: Yup.array().of(
      Yup.object().shape({
        responsabile: Yup.string().required(t(requiredText)),
        commenti: Yup.string().required(t(requiredText)),
        data: Yup.string().required(t(requiredText)),
        sign: Yup.string().nullable(),
      })
    ),
  });

export const closureValidationSchema = (t: typeof i18n.t) =>
  Yup.object().shape({
    fineLavoriDate: Yup.string().required(t(requiredText)),
    fineLavoriTime: Yup.string().required(t(requiredText)),
    approvazioneLavoriACaldo: Yup.object().when('attivitaAltoRischio', {
      is: (values) => values?.includes(IAttivitaAltoRischio.lavoriACaldo),
      then: Yup.object().shape({
        name: Yup.string().required(t(requiredText)),
        date: Yup.date().required(t(requiredText)),
        hour: Yup.date().required(t(requiredText)),
        sign: Yup.string().required(t(requiredSignText)).nullable(),
      }),
      otherwise: Yup.object(),
    }),
    closureType: Yup.string().oneOf(['form', 'attachment']).required(t(requiredText)),
    note: Yup.string().when('closureType', {
      is: (value) => value === 'form',
      then: Yup.string().required(t(requiredText)),
      otherwise: Yup.string(),
    }),
    riconsegnaName: Yup.string().when('closureType', {
      is: (value) => value === 'form',
      then: Yup.string().required(t(requiredText)),
      otherwise: Yup.string(),
    }),
    riconsegnaSign: Yup.string().when('closureType', {
      is: (value) => value === 'form',
      then: Yup.string().required(t(requiredSignText)).nullable(),
      otherwise: Yup.string().nullable(),
    }),
    prendeInConsegnaName: Yup.string().when('closureType', {
      is: (value) => value === 'form',
      then: Yup.string().required(t(requiredText)),
      otherwise: Yup.string(),
    }),
    prendeInConsegnaSign: Yup.string().when('closureType', {
      is: (value) => value === 'form',
      then: Yup.string().required(t(requiredSignText)).nullable(),
      otherwise: Yup.string().nullable(),
    }),
    responsabileAltoRischioName: Yup.string().when('closureType', {
      is: (value) => value === 'form',
      then: Yup.string().required(t(requiredText)),
      otherwise: Yup.string(),
    }),
    responsabileAltoRischioSign: Yup.string().when('closureType', {
      is: (value) => value === 'form',
      then: Yup.string().required(t(requiredSignText)).nullable(),
      otherwise: Yup.string().nullable(),
    }),
    ehsClosureFile: Yup.string().when('closureType', {
      is: (value) => value === 'attachment',
      then: Yup.string().required(t(requiredText)),
      otherwise: Yup.string().nullable(),
    }),
  });
